/**
 * Icons.jsx
 * Inline stroke icons. Kept as one small set rather than a dependency —
 * the whole app needs about twenty glyphs and they ship as ~2KB of markup.
 *
 * Every icon inherits currentColor and sizes from the `size` prop.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
});

export const Crop = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M6.5 2v15.5H22" />
    <path d="M2 6.5h15.5V22" />
  </svg>
);

export const Upload = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M12 16V4" />
    <path d="m7.5 8.5 4.5-4.5 4.5 4.5" />
    <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
  </svg>
);

export const Download = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M12 4v12" />
    <path d="m7.5 11.5 4.5 4.5 4.5-4.5" />
    <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
  </svg>
);

export const Table = ({ size = 18 }) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9.5h18M9 9.5V20" />
  </svg>
);

export const Recycle = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M4.5 10.5 7 6.2a2 2 0 0 1 3.4 0L12 8.7" />
    <path d="m3.4 13.6 1.1-3.1 3.1 1" />
    <path d="M19.5 13.5 17 17.8a2 2 0 0 1-1.7 1H12" />
    <path d="m16.4 21 2.9-1.6-1.6-2.8" />
    <path d="M14 6.2 16.6 10.6" />
    <path d="m12.6 4.8 3.2.2-.4 3.2" />
    <path d="M9.5 18.8H6.6a2 2 0 0 1-1.7-3l1.4-2.4" />
  </svg>
);

export const Hash = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M9.5 3.5 7.5 20.5M16.5 3.5l-2 17M4 8.5h16M3.5 15.5h16" />
  </svg>
);

export const Eye = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="2.8" />
  </svg>
);

export const Users = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.4a3.2 3.2 0 0 1 0 5.2" />
    <path d="M17.5 14.6a5.5 5.5 0 0 1 3 4.9" />
  </svg>
);

export const Send = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M21 3 10.5 13.5" />
    <path d="M21 3 14.5 21l-4-7.5L3 9.5 21 3Z" />
  </svg>
);

export const Home = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5.5 9.5V21h13V9.5" />
  </svg>
);

export const Plus = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const Calendar = ({ size = 18 }) => (
  <svg {...base(size)}>
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

export const Image = ({ size = 18 }) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="16" rx="3" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="m4 17 4.5-4.5 3.5 3.5 3-2.5L20 18" />
  </svg>
);

export const Tag = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M3 11.5V4.5A1.5 1.5 0 0 1 4.5 3h7L21 12.5 12.5 21 3 11.5Z" />
    <circle cx="7.6" cy="7.6" r="1.4" />
  </svg>
);

export const Chart = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </svg>
);

export const Database = ({ size = 18 }) => (
  <svg {...base(size)}>
    <ellipse cx="12" cy="6" rx="8" ry="3" />
    <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
    <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
  </svg>
);

export const Link = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M10 13a5 5 0 0 0 7.1.1l2-2A5 5 0 0 0 12 4l-1.2 1.2" />
    <path d="M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.2-1.2" />
  </svg>
);

export const User = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="8.5" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </svg>
);

export const Clock = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.5l3.5 2" />
  </svg>
);

export const Bell = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9Z" />
    <path d="M10 18a2 2 0 0 0 4 0" />
  </svg>
);

export const Check = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.3 2.4 2.4 4.6-4.9" />
  </svg>
);

export const Refresh = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M20 11a8 8 0 0 0-13.7-5.1L3 9" />
    <path d="M4 13a8 8 0 0 0 13.7 5.1L21 15" />
    <path d="M3 4v5h5M21 20v-5h-5" />
  </svg>
);

export const Sun = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
  </svg>
);

export const Moon = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
  </svg>
);

export const Chevron = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const ChevronRight = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="m9 6 6 6-6 6" />
  </svg>
);

export const Help = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.6 9.4a2.5 2.5 0 0 1 4.9.6c0 1.7-2.5 2-2.5 3.5" />
    <path d="M12 17.2h.01" />
  </svg>
);

export const Settings = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2 2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 10 4.1a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 21 11a2 2 0 1 1 0 4Z" />
  </svg>
);

export const Warning = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M12 4 2.8 20h18.4L12 4Z" />
    <path d="M12 10v4M12 17h.01" />
  </svg>
);

export const Edit = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M4 20h4.5L20 8.5a2.1 2.1 0 0 0-3-3L5.5 17v3Z" />
    <path d="M14.5 6.5l3 3" />
  </svg>
);

export const Layers = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M12 3 3 8l9 5 9-5-9-5Z" />
    <path d="m3 13 9 5 9-5" />
  </svg>
);

export const Clipboard = ({ size = 18 }) => (
  <svg {...base(size)}>
    <rect x="8" y="3" width="8" height="4" rx="1" />
    <path d="M9 5H6.5A1.5 1.5 0 0 0 5 6.5v13A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 17.5 5H15" />
  </svg>
);

export const Chat = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M20 12a7 7 0 0 1-7 7H8l-4 3v-5.5A7 7 0 0 1 8 5h5a7 7 0 0 1 7 7Z" />
  </svg>
);

export const Megaphone = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1Z" />
    <path d="M15 8.5a4 4 0 0 1 0 7" />
    <path d="M18 6a8 8 0 0 1 0 12" />
  </svg>
);

export const Server = ({ size = 18 }) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="7" rx="2" />
    <rect x="3" y="13" width="18" height="7" rx="2" />
    <path d="M7 7.5h.01M7 16.5h.01" />
  </svg>
);

export const Film = ({ size = 18 }) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4" />
  </svg>
);

export const Menu = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

export const Close = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

/* ============================================
   PLATFORM MARKS
   These are the two platforms the app publishes to. They are filled rather
   than stroked, because a brand mark drawn in hairlines stops being
   recognisable at 14px.
   ============================================ */

export const Facebook = ({ size = 18 }) => (
  <svg {...base(size)} fill="currentColor" stroke="none">
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.89 3.77-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.89h-2.33v6.99A10 10 0 0 0 22 12Z" />
  </svg>
);

export const Instagram = ({ size = 18 }) => (
  <svg {...base(size)} fill="currentColor" stroke="none">
    <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.73 3.73 0 0 1-1.38-.9 3.73 3.73 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07Zm0 1.98c-3.15 0-3.5.01-4.74.07-1.14.05-1.76.24-2.18.4-.54.22-.93.47-1.34.88-.4.4-.66.8-.87 1.34-.16.41-.35 1.03-.4 2.18-.06 1.23-.07 1.6-.07 4.74s.01 3.5.07 4.74c.05 1.14.24 1.76.4 2.18.22.54.47.93.87 1.34.41.4.8.66 1.34.87.42.16 1.04.35 2.18.4 1.24.06 1.6.07 4.74.07s3.5-.01 4.74-.07c1.14-.05 1.76-.24 2.18-.4.54-.21.93-.47 1.34-.87.4-.41.66-.8.87-1.34.16-.42.35-1.04.4-2.18.06-1.24.07-1.6.07-4.74s-.01-3.51-.07-4.74c-.05-1.15-.24-1.77-.4-2.18a3.6 3.6 0 0 0-.87-1.34 3.6 3.6 0 0 0-1.34-.88c-.42-.16-1.04-.35-2.18-.4-1.24-.06-1.6-.07-4.74-.07Zm0 3.37a5.05 5.05 0 1 1 0 10.1 5.05 5.05 0 0 1 0-10.1Zm0 8.33a3.28 3.28 0 1 0 0-6.56 3.28 3.28 0 0 0 0 6.56Zm6.43-8.55a1.18 1.18 0 1 1-2.36 0 1.18 1.18 0 0 1 2.36 0Z" />
  </svg>
);

/* ============================================
   DASHBOARD GLYPHS
   ============================================ */

/** AI. Four-pointed sparkle, so it never reads as a rating star. */
export const Sparkle = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M12 3.5 13.7 9l5.5 1.7-5.5 1.7L12 18l-1.7-5.6L4.8 10.7 10.3 9 12 3.5Z" />
    <path d="M18.5 3v3M20 4.5h-3M5.5 16v2.5M6.75 17.25h-2.5" />
  </svg>
);

/** Rising line. Used only next to a figure that has actually risen. */
export const TrendUp = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M3.5 16.5 9 11l3.5 3.5L20.5 6.5" />
    <path d="M15.5 6.5h5v5" />
  </svg>
);

export const TrendDown = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M3.5 7.5 9 13l3.5-3.5L20.5 17.5" />
    <path d="M15.5 17.5h5v-5" />
  </svg>
);

/** Heartbeat. The health of a connection rather than the health of a person. */
export const Pulse = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M2.5 12h4l2-5.5 3.5 11 2.5-7 1.8 3.5h5.2" />
  </svg>
);

/* ============================================
   COPILOT GLYPHS
   ============================================ */

/** A goal. Concentric rings rather than a flag: the point is the aim, not the finish. */
export const Target = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="4.5" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
  </svg>
);

/** An idea. Used for the proposed posts, never for a measured finding. */
export const Bulb = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M9.2 17.5h5.6M10 20.5h4" />
    <path d="M12 3.5a6 6 0 0 0-3.6 10.8c.5.4.8 1 .8 1.6v.6h5.6v-.6c0-.6.3-1.2.8-1.6A6 6 0 0 0 12 3.5Z" />
  </svg>
);

/**
 * One thing becoming several. Used for Repurpose, where a single source fans
 * out into a version per channel.
 */
export const Fanout = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="4.5" cy="12" r="2.2" />
    <circle cx="19.5" cy="5.5" r="2.2" />
    <circle cx="19.5" cy="12" r="2.2" />
    <circle cx="19.5" cy="18.5" r="2.2" />
    <path d="M6.7 12h3.8M10.5 12v-6.5h6.8M10.5 12h6.8M10.5 12v6.5h6.8" />
  </svg>
);
